import React from "react";

import CopyableIdentifier from "./CopyableIdentifier";
import TraceableIdentifierLink from "./TraceableIdentifierLink";

export type LotTraceType = "manufactured" | "supplier";

type InventoryLotOrigin = {
  trace_type?: unknown;
  receiving_invoice_id?: unknown;
  receiving_item_id?: unknown;
};

type TraceableLotNumberProps = {
  lotNumber: string | number | null | undefined;
  traceType: LotTraceType | null | undefined;
  className?: string;
  valueClassName?: string;
};

export function buildLotTracePath(
  lotNumber: string | number,
  traceType: LotTraceType,
) {
  const searchParams = new URLSearchParams({
    lookup: String(lotNumber),
    type: traceType,
  });

  return `/traceability?${searchParams.toString()}`;
}

/** Resolve traceability from the inventory record's origin, not its current screen. */
export function getInventoryLotTraceType(
  item: InventoryLotOrigin | null | undefined,
): LotTraceType | null {
  if (item?.trace_type === "supplier" || item?.trace_type === "manufactured") {
    return item.trace_type;
  }

  const originIds = [item?.receiving_invoice_id, item?.receiving_item_id].filter(
    (value) => value !== undefined && value !== null && String(value).trim() !== "",
  );

  if (originIds.length === 0) return null;

  const parsedOriginIds = originIds.map(Number);
  if (parsedOriginIds.some((value) => !Number.isFinite(value))) return null;

  return parsedOriginIds.some((value) => value > 0)
    ? "supplier"
    : "manufactured";
}

/** A lot number that keeps its copy action and opens the matching trace report. */
export default function TraceableLotNumber({
  lotNumber,
  traceType,
  className,
  valueClassName,
}: TraceableLotNumberProps) {
  if (!traceType) {
    return (
      <CopyableIdentifier
        value={lotNumber}
        description="lot number"
        className={className}
        valueClassName={valueClassName}
      />
    );
  }

  const traceDescription =
    traceType === "supplier" ? "supplier item" : "manufactured product";

  return (
    <TraceableIdentifierLink
      value={lotNumber}
      description="lot number"
      buildPath={(value) => buildLotTracePath(value, traceType)}
      buildTitle={(value) => `Trace ${traceDescription} lot ${value}`}
      className={className}
      valueClassName={valueClassName}
    />
  );
}
